import type { OwnersService } from '../owners/owners.service';
import type { GlobalVendorsService } from '../global-vendors/global-vendors.service';
import type { VendorsService } from '../vendors/vendors.service';
import type { LettingsService } from '../lettings/lettings.service';
import type { ItemsService } from '../items/items.service';
import type { UnitsService } from '../units/units.service';
import type { ProposalsService } from '../proposals/proposals.service';
import type { ProposalItemsService } from '../proposal-items/proposal-items.service';
import type { BidsService } from '../bids/bids.service';
import type { BidItemsService } from '../bid-items/bid-items.service';
import type { PayEstimatesService } from '../pay-estimates/pay-estimates.service';
import type { PayEstimateItemsService } from '../pay-estimate-items/pay-estimate-items.service';
import type { ProposalExceptionsService } from '../proposal-exceptions/proposal-exceptions.service';
import type { CountiesService } from '../counties/counties.service';
import type { ProposalCountiesService } from '../proposal-counties/proposal-counties.service';
import type { DistrictsService } from '../districts/districts.service';
import type { DistrictCountiesService } from '../district-counties/district-counties.service';
import type { ProposalDistrictsService } from '../proposal-districts/proposal-districts.service';
import type { StatesService } from '../states/states.service';
import type { ItemCollectionsService } from '../item-collections/item-collections.service';
import type { LiquidAsphaltService } from '../liquid-asphalt/liquid-asphalt.service';
import type { BidItemLiquidAcInput } from '../liquid-asphalt/liquid-asphalt.repository';
import {
  coerceAdjustmentStatus,
  coerceLiquidAcPattern,
  mapClassificationSource,
} from '../liquid-asphalt/liquid-ac-pattern';
import type {
  TableBatchResult,
  EntitySyncStats,
  SnowflakeOwner,
  SnowflakeGlobalVendor,
  SnowflakeVendor,
  SnowflakeLetting,
  SnowflakeItem,
  SnowflakeProposal,
  SnowflakeProposalItem,
  SnowflakeBid,
  SnowflakeBidItem,
  SnowflakePayEstimate,
  SnowflakePayEstimateItem,
  SnowflakeBidItemLiquidAc,
  SnowflakeException,
  SnowflakeCounty,
  SnowflakeProposalCounty,
  SnowflakeDistrict,
  SnowflakeDistrictCounty,
  SnowflakeProposalDistrict,
  SnowflakeState,
  SnowflakeCollectionMapping,
} from './warehouse.types';
import type { InsertOwner } from '../owners';
import type { GlobalVendorInsert } from '@edgevanta/db/drizzle';
import type { InsertVendor } from '../vendors';
import type { InsertLetting } from '../lettings';
import type { InsertItem } from '../items';
import type { InsertProposal } from '../proposals';
import type { InsertCounty } from '../counties';
import type { InsertDistrict } from '../districts';
import type { StateInsert } from '@edgevanta/db/drizzle';
import type { WarehouseRepository } from './warehouse.repository';
import type { IWarehouseSyncLogger } from './warehouse-job-logger';
import type { ProposalTypesService } from '../proposal-types/proposal-types.service';
import type { WarehouseSyncJobRepository } from '../warehouse-sync/warehouse-sync-job.repository';
import type { SyncValidationResult } from '../warehouse-sync/types';
/**
 * Safely parses a date value from Snowflake, returning null for invalid or missing dates.
 * This prevents RangeError when attempting to convert invalid dates to timestamps.
 *
 * @param dateValue - Date value from Snowflake (may be string, Date, number, undefined, null, or invalid)
 * @returns Valid Date object or null
 */
function safeParseDate(
  dateValue: string | Date | number | undefined | null,
): Date | null {
  if (!dateValue) {
    return null;
  }

  // If it's already a Date object, validate it
  if (dateValue instanceof Date) {
    return Number.isNaN(dateValue.getTime()) ? null : dateValue;
  }

  // If it's a string, trim and check if empty
  if (typeof dateValue === 'string' && dateValue.trim() === '') {
    return null;
  }

  // Try to parse as Date
  const date = new Date(dateValue);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * The Snowflake driver returns an ARRAY column as a parsed array OR a JSON
 * string (same as `units.variants`). Normalize either form to `string[]`,
 * falling back to `null` when absent or when the value isn't an array.
 */
function parseStringArray(raw: string[] | string | null): string[] | null {
  if (raw == null) {
    return null;
  }
  if (Array.isArray(raw)) {
    return raw.filter((v): v is string => typeof v === 'string');
  }
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return null;
    }
    return parsed.filter((v): v is string => typeof v === 'string');
  } catch {
    return null;
  }
}

/**
 * Map a Snowflake liquid-AC bid-item row to the repository input. Shared by the
 * per-owner sync (WarehouseService) and the per-state CLI so both paths write
 * the SAME 14 legacy + 18 canonical columns — a single source of truth prevents
 * one path silently dropping canonical fields (which the upsert would then
 * NULL-overwrite). Enum strings are coerced defensively; classification source
 * and date strings are normalized.
 */
export function snowflakeBidItemLiquidAcToInput(
  r: SnowflakeBidItemLiquidAc,
): BidItemLiquidAcInput {
  return {
    bidItemExternalId: r.bidItemExternalId,
    state: r.state,
    pattern: r.pattern,
    mixFamily: r.mixFamily,
    liquidAcPct: r.liquidAcPct,
    pgGrade: r.pgGrade,
    needsLiquidAdjustment: r.needsLiquidAdjustment,
    historicalIndexAtBid: r.historicalIndexAtBid,
    historicalIndexGrade: r.historicalIndexGrade,
    historicalIndexRegion: r.historicalIndexRegion,
    historicalIndexIsProxy: r.historicalIndexIsProxy,
    basis: r.basis,
    shouldAdjust: r.shouldAdjust,
    adjustmentWarning: r.adjustmentWarning,
    liquidAcPattern: coerceLiquidAcPattern(r.liquidAcPattern),
    needsLiquidAsphaltAdjustment: r.needsLiquidAsphaltAdjustment,
    classificationSource: mapClassificationSource(r.classificationSource),
    classificationConfidence: r.classificationConfidence,
    liquidAcSpecRuleId: r.liquidAcSpecRuleId,
    liquidAcSpecVersion: r.liquidAcSpecVersion,
    effectiveFrom: safeParseDate(r.effectiveFrom),
    effectiveTo: safeParseDate(r.effectiveTo),
    adjustmentStatus: coerceAdjustmentStatus(r.adjustmentStatus),
    adjustmentWarnings: parseStringArray(r.adjustmentWarnings),
    requestedItemGrade: r.requestedItemGrade,
    resolvedIndexGrade: r.resolvedIndexGrade,
    gradeFallbackUsed: r.gradeFallbackUsed,
    gradeFallbackReason: r.gradeFallbackReason,
    indexAtBidRowId: r.indexAtBidRowId,
    indexAtBidMonth: safeParseDate(r.indexAtBidMonth),
    indexAtBidSource: r.indexAtBidSource,
    conversionFactorSnapshot: r.conversionFactorSnapshot,
  };
}

export class WarehouseService {
  constructor(
    private readonly warehouseRepository: WarehouseRepository,
    private readonly ownersService: OwnersService,
    private readonly statesService: StatesService,
    private readonly globalVendorsService: GlobalVendorsService,
    private readonly vendorsService: VendorsService,
    private readonly lettingsService: LettingsService,
    private readonly itemsService: ItemsService,
    private readonly proposalsService: ProposalsService,
    private readonly proposalItemsService: ProposalItemsService,
    private readonly bidsService: BidsService,
    private readonly bidItemsService: BidItemsService,
    private readonly proposalExceptionsService: ProposalExceptionsService,
    private readonly countiesService: CountiesService,
    private readonly proposalCountiesService: ProposalCountiesService,
    private readonly districtsService: DistrictsService,
    private readonly districtCountiesService: DistrictCountiesService,
    private readonly proposalDistrictsService: ProposalDistrictsService,
    private readonly itemCollectionsService: ItemCollectionsService,
    private readonly unitsService: UnitsService,
    private readonly liquidAsphaltService: LiquidAsphaltService,
    private readonly payEstimatesService: PayEstimatesService,
    private readonly payEstimateItemsService: PayEstimateItemsService,
    private readonly proposalTypesService: ProposalTypesService,
    private readonly logger: IWarehouseSyncLogger,
    private readonly syncJobRepository?: WarehouseSyncJobRepository,
  ) {}

  public insertOnly = false;

  getJobLogger(): IWarehouseSyncLogger {
    return this.logger;
  }

  async getAllAvailableOwners(): Promise<SnowflakeOwner[]> {
    return this.warehouseRepository.fetchAllOwners();
  }

  async deleteSpecificOwner(
    ownerExternalId: string,
  ): Promise<{ deletedCount: number }> {
    const startTime = Date.now();
    this.logger.info('Starting deletion of specific owner', {
      ownerExternalId,
    });

    const allOwners = await this.ownersService.getAllOwnersForWarehouseSync();

    const ownerToDelete = allOwners.find(
      (owner) => owner.externalId === ownerExternalId,
    );

    if (!ownerToDelete) {
      this.logger.warn(`Owner not found with externalId: ${ownerExternalId}`);
      return { deletedCount: 0 };
    }

    this.logger.info(`Deleting owner: ${ownerToDelete.name}`, {
      ownerId: ownerToDelete.id,
      externalId: ownerToDelete.externalId,
    });

    await this.ownersService.deleteOwner(ownerToDelete.id);

    // Clear sync job records so UI shows correct status
    if (this.syncJobRepository) {
      const deletedJobs =
        await this.syncJobRepository.deleteByOwnerExternalId(ownerExternalId);
      this.logger.info(`Cleared ${deletedJobs} sync job records`, {
        ownerExternalId,
      });
    }

    const duration = Date.now() - startTime;
    this.logger.info(
      `Deletion completed. Deleted owner "${ownerToDelete.name}" and all related data`,
      {
        deletedCount: 1,
        duration,
        ownerId: ownerToDelete.id,
        ownerName: ownerToDelete.name,
      },
    );

    return { deletedCount: 1 };
  }

  async cleanupUnselectedOwners(
    selectedExternalIds: string[],
  ): Promise<{ deletedCount: number }> {
    const startTime = Date.now();
    this.logger.info('Starting cleanup of unselected owners', {
      selectedExternalIds,
    });

    const allOwners = await this.ownersService.getAllOwnersForWarehouseSync();

    this.logger.info('Retrieved all owners for organization', {
      totalOwners: allOwners.length,
      ownerExternalIds: allOwners.map((o) => ({
        id: o.id,
        name: o.name,
        externalId: o.externalId,
      })),
    });

    // `organization_id` is the provenance fact; `external_id` only correlates.
    const ownersToDelete = allOwners.filter(
      (owner) =>
        owner.organizationId === null &&
        (!owner.externalId || !selectedExternalIds.includes(owner.externalId)),
    );

    this.logger.info(`Found ${ownersToDelete.length} owners to remove`, {
      totalOwners: allOwners.length,
      selectedCount: selectedExternalIds.length,
      toDeleteCount: ownersToDelete.length,
      ownersToDelete: ownersToDelete.map((o) => ({
        id: o.id,
        name: o.name,
        externalId: o.externalId,
      })),
    });

    for (const owner of ownersToDelete) {
      this.logger.info(`Deleting owner: ${owner.name}`, {
        ownerId: owner.id,
        externalId: owner.externalId,
      });
      await this.ownersService.deleteOwner(owner.id);

      // Clear sync job records so UI shows correct status
      if (this.syncJobRepository && owner.externalId) {
        const deletedJobs =
          await this.syncJobRepository.deleteByOwnerExternalId(
            owner.externalId,
          );
        this.logger.info(`Cleared ${deletedJobs} sync job records`, {
          ownerExternalId: owner.externalId,
        });
      }
    }

    const duration = Date.now() - startTime;
    this.logger.info(
      `Cleanup completed. Deleted ${ownersToDelete.length} owners and all related data`,
      {
        deletedCount: ownersToDelete.length,
        duration,
      },
    );

    return { deletedCount: ownersToDelete.length };
  }

  async syncTableBatch(
    ownerExternalId: string,
    table:
      | 'owners'
      | 'states'
      | 'globalVendors'
      | 'contractTypes'
      | 'projectTypes'
      | 'vendors'
      | 'lettings'
      | 'items'
      | 'proposals'
      | 'counties'
      | 'districts'
      | 'districtCounties'
      | 'proposalCounties'
      | 'proposalDistricts'
      | 'proposalItems'
      | 'bids'
      | 'bidItems'
      | 'bidItemLiquidAc'
      | 'exceptions'
      | 'payEstimates'
      | 'payEstimateItems'
      | 'collectionMappings',
    limit: number,
    offset: number,
    afterCursor?: string,
  ): Promise<TableBatchResult> {
    const batchNumber = Math.floor(offset / limit) + 1;
    const rangeStart = offset.toLocaleString();
    const rangeEnd = (offset + limit).toLocaleString();

    this.logger.info(
      `Batch ${batchNumber} (${rangeStart} → ${rangeEnd}): Fetching from Snowflake`,
      {
        table,
        ownerExternalId,
        limit,
        offset,
      },
    );

    const stats: EntitySyncStats = { created: 0, updated: 0, errors: 0 };
    let lastCursor: string | null = null;

    try {
      switch (table) {
        case 'owners': {
          const data = await this.warehouseRepository.fetchOwnersBatch(
            ownerExternalId,
            limit,
            offset,
            afterCursor,
          );
          this.logger.info(
            `Batch ${batchNumber}: Fetched ${data.length.toLocaleString()} owners, inserting into platform...`,
          );
          await this.syncOwners(data, stats);
          if (data.length > 0) {
            lastCursor = data[data.length - 1]?.externalId ?? null;
          }
          break;
        }
        case 'states': {
          const data = await this.warehouseRepository.fetchStatesBatch(
            limit,
            offset,
            afterCursor,
          );
          this.logger.info(
            `Batch ${batchNumber}: Fetched ${data.length.toLocaleString()} states, inserting into platform...`,
          );
          await this.syncStates(data, stats);
          if (data.length > 0) {
            lastCursor = data[data.length - 1]?.externalId ?? null;
          }
          break;
        }
        case 'globalVendors': {
          const data = await this.warehouseRepository.fetchGlobalVendorsBatch(
            limit,
            offset,
            afterCursor,
          );
          this.logger.info(
            `Batch ${batchNumber}: Fetched ${data.length.toLocaleString()} global vendors, inserting into platform...`,
          );
          await this.syncGlobalVendors(data, stats);
          if (data.length > 0) {
            lastCursor = data[data.length - 1]?.externalId ?? null;
          }
          break;
        }
        case 'contractTypes': {
          const data = await this.warehouseRepository.fetchContractTypesBatch(
            limit,
            offset,
          );
          this.logger.info(
            `Batch ${batchNumber}: Fetched ${data.length.toLocaleString()} contract types, inserting into platform...`,
          );
          for (const row of data) {
            try {
              await this.proposalTypesService.syncContractType(row);
              stats.created++;
            } catch (error) {
              stats.errors++;
              this.logger.error(
                `Failed to sync contract type ${row.code}`,
                error instanceof Error ? error : new Error(String(error)),
              );
            }
          }
          // No cursor: these dims are a handful of rows and always fit one
          // batch. Returning a cursor would loop forever, since the fetch pages
          // by offset and the job runner re-sends offset 0 with the cursor.
          break;
        }
        case 'projectTypes': {
          const data = await this.warehouseRepository.fetchProjectTypesBatch(
            limit,
            offset,
          );
          this.logger.info(
            `Batch ${batchNumber}: Fetched ${data.length.toLocaleString()} project types, inserting into platform...`,
          );
          for (const row of data) {
            try {
              await this.proposalTypesService.syncProjectType(row);
              stats.created++;
            } catch (error) {
              stats.errors++;
              this.logger.error(
                `Failed to sync project type ${row.code}`,
                error instanceof Error ? error : new Error(String(error)),
              );
            }
          }
          // No cursor: these dims are a handful of rows and always fit one
          // batch. Returning a cursor would loop forever, since the fetch pages
          // by offset and the job runner re-sends offset 0 with the cursor.
          break;
        }
        case 'vendors': {
          const data = await this.warehouseRepository.fetchVendorsBatch(
            ownerExternalId,
            limit,
            offset,
            afterCursor,
          );
          this.logger.info(
            `Batch ${batchNumber}: Fetched ${data.length.toLocaleString()} vendors, inserting into platform...`,
          );
          await this.syncVendors(data, stats);
          if (data.length > 0) {
            lastCursor = data[data.length - 1]?.externalId ?? null;
          }
          break;
        }
        case 'lettings': {
          const data = await this.warehouseRepository.fetchLettingsBatch(
            ownerExternalId,
            limit,
            offset,
            afterCursor,
          );
          this.logger.info(
            `Batch ${batchNumber}: Fetched ${data.length.toLocaleString()} lettings, inserting into platform...`,
          );
          await this.syncLettings(data, stats);
          if (data.length > 0) {
            lastCursor = data[data.length - 1]?.externalId ?? null;
          }
          break;
        }
        case 'items': {
          const data = await this.warehouseRepository.fetchItemsBatch(
            ownerExternalId,
            limit,
            offset,
            afterCursor,
          );
          this.logger.info(
            `Batch ${batchNumber}: Fetched ${data.length.toLocaleString()} items, inserting into platform...`,
          );
          await this.syncItems(data, stats);
          if (data.length > 0) {
            lastCursor = data[data.length - 1]?.externalId ?? null;
          }
          break;
        }
        case 'collectionMappings': {
          const data =
            await this.warehouseRepository.fetchCollectionMappingsBatchByOwner(
              ownerExternalId,
              limit,
              offset,
              afterCursor,
            );
          this.logger.info(
            `Batch ${batchNumber}: Fetched ${data.length.toLocaleString()} collection mappings, inserting into platform...`,
          );
          await this.syncCollectionMappings(data, stats, ownerExternalId);
          if (data.length > 0) {
            lastCursor = data[data.length - 1]?.externalId ?? null;
          }
          break;
        }
        case 'proposals': {
          const data = await this.warehouseRepository.fetchProposalsBatch(
            ownerExternalId,
            limit,
            offset,
            afterCursor,
          );
          this.logger.info(
            `Batch ${batchNumber}: Fetched ${data.length.toLocaleString()} proposals, inserting into platform...`,
          );
          await this.syncProposals(data, stats);
          if (data.length > 0) {
            lastCursor = data[data.length - 1]?.externalId ?? null;
          }
          break;
        }
        case 'counties': {
          const data = await this.warehouseRepository.fetchCountiesBatch(
            limit,
            offset,
            afterCursor,
          );
          this.logger.info(
            `Batch ${batchNumber}: Fetched ${data.length.toLocaleString()} counties, inserting into platform...`,
          );
          await this.syncCounties(data, stats);
          if (data.length > 0) {
            lastCursor = data[data.length - 1]?.externalId ?? null;
          }
          break;
        }
        case 'proposalCounties': {
          const data =
            await this.warehouseRepository.fetchProposalCountiesBatch(
              ownerExternalId,
              limit,
              offset,
              afterCursor,
            );
          this.logger.info(
            `Batch ${batchNumber}: Fetched ${data.length.toLocaleString()} proposal counties, inserting into platform...`,
          );
          await this.syncProposalCounties(data, stats);
          if (data.length > 0) {
            const last = data[data.length - 1];
            lastCursor = last
              ? `${last.proposalExternalId}|${last.countyExternalId}`
              : null;
          }
          break;
        }
        case 'districts': {
          const data = await this.warehouseRepository.fetchDistrictsBatch(
            limit,
            offset,
            afterCursor,
          );
          this.logger.info(
            `Batch ${batchNumber}: Fetched ${data.length.toLocaleString()} districts, inserting into platform...`,
          );
          await this.syncDistricts(data, stats);
          if (data.length > 0) {
            lastCursor = data[data.length - 1]?.externalId ?? null;
          }
          break;
        }
        case 'districtCounties': {
          const data =
            await this.warehouseRepository.fetchDistrictCountiesBatch(
              limit,
              offset,
              afterCursor,
            );
          this.logger.info(
            `Batch ${batchNumber}: Fetched ${data.length.toLocaleString()} district counties, inserting into platform...`,
          );
          await this.syncDistrictCounties(data, stats);
          if (data.length > 0) {
            const last = data[data.length - 1];
            lastCursor = last
              ? `${last.districtExternalId}|${last.countyExternalId}`
              : null;
          }
          break;
        }
        case 'proposalDistricts': {
          const data =
            await this.warehouseRepository.fetchProposalDistrictsBatch(
              ownerExternalId,
              limit,
              offset,
              afterCursor,
            );
          this.logger.info(
            `Batch ${batchNumber}: Fetched ${data.length.toLocaleString()} proposal districts, inserting into platform...`,
          );
          await this.syncProposalDistricts(data, stats);
          if (data.length > 0) {
            const last = data[data.length - 1];
            lastCursor = last
              ? `${last.proposalExternalId}|${last.districtExternalId}`
              : null;
          }
          break;
        }
        case 'proposalItems': {
          const data = await this.warehouseRepository.fetchProposalItemsBatch(
            ownerExternalId,
            limit,
            offset,
            afterCursor,
          );
          this.logger.info(
            `Batch ${batchNumber}: Fetched ${data.length.toLocaleString()} proposal items, inserting into platform...`,
          );
          await this.syncProposalItems(data, stats);
          if (data.length > 0) {
            lastCursor = data[data.length - 1]?.externalId ?? null;
          }
          break;
        }
        case 'bids': {
          const data = await this.warehouseRepository.fetchBidsBatch(
            ownerExternalId,
            limit,
            offset,
            afterCursor,
          );
          this.logger.info(
            `Batch ${batchNumber}: Fetched ${data.length.toLocaleString()} bids, inserting into platform...`,
          );
          await this.syncBids(data, stats);
          if (data.length > 0) {
            lastCursor = data[data.length - 1]?.externalId ?? null;
          }
          break;
        }
        case 'bidItems': {
          const data = await this.warehouseRepository.fetchBidItemsBatch(
            ownerExternalId,
            limit,
            offset,
            afterCursor,
          );
          this.logger.info(
            `Batch ${batchNumber}: Fetched ${data.length.toLocaleString()} bid items, inserting into platform...`,
          );
          await this.syncBidItems(data, stats);
          if (data.length > 0) {
            lastCursor = data[data.length - 1]?.externalId ?? null;
          }
          break;
        }
        case 'bidItemLiquidAc': {
          const data =
            await this.warehouseRepository.fetchBidItemLiquidAcBatchByOwner(
              ownerExternalId,
              limit,
              offset,
            );
          this.logger.info(
            `Batch ${batchNumber}: Fetched ${data.length.toLocaleString()} liquid AC classifications, inserting into platform...`,
          );
          await this.syncBidItemLiquidAc(data, stats);
          break;
        }
        case 'exceptions': {
          const data = await this.warehouseRepository.fetchExceptionsBatch(
            ownerExternalId,
            limit,
            offset,
            afterCursor,
          );
          this.logger.info(
            `Batch ${batchNumber}: Fetched ${data.length.toLocaleString()} exceptions, updating proposals...`,
          );
          await this.syncExceptions(data, stats);
          if (data.length > 0) {
            lastCursor = data[data.length - 1]?.externalId ?? null;
          }
          break;
        }
        case 'payEstimates': {
          const data = await this.warehouseRepository.fetchPayEstimatesBatch(
            ownerExternalId,
            limit,
            offset,
            afterCursor,
          );
          this.logger.info(
            `Batch ${batchNumber}: Fetched ${data.length.toLocaleString()} pay estimates, inserting into platform...`,
          );
          await this.syncPayEstimates(data, stats);
          if (data.length > 0) {
            lastCursor = data[data.length - 1]?.externalId ?? null;
          }
          break;
        }
        case 'payEstimateItems': {
          const data =
            await this.warehouseRepository.fetchPayEstimateItemsBatch(
              ownerExternalId,
              limit,
              offset,
              afterCursor,
            );
          this.logger.info(
            `Batch ${batchNumber}: Fetched ${data.length.toLocaleString()} pay estimate items, inserting into platform...`,
          );
          await this.syncPayEstimateItems(data, stats);
          if (data.length > 0) {
            lastCursor = data[data.length - 1]?.externalId ?? null;
          }
          break;
        }
      }

      this.logger.info(
        `Batch ${batchNumber}: Completed - Created: ${stats.created.toLocaleString()}, Errors: ${stats.errors.toLocaleString()}`,
      );
      return { ...stats, lastCursor };
    } catch (error) {
      this.logger.error(`Error syncing ${table} batch`, error);
      throw error;
    }
  }

  private async syncOwners(
    owners: SnowflakeOwner[],
    stats: EntitySyncStats,
  ): Promise<void> {
    if (owners.length === 0) {
      return;
    }

    try {
      const allStates = await this.statesService.getStates();
      const stateIdByAbbreviation = new Map(
        allStates.map((s) => [s.abbreviation, s.id]),
      );

      const toInsert: InsertOwner[] = [];
      let unresolvedStateCount = 0;
      for (const owner of owners) {
        const stateId = stateIdByAbbreviation.get(owner.state);
        if (!stateId) {
          unresolvedStateCount++;
          continue;
        }
        toInsert.push({
          externalId: owner.externalId,
          name: owner.name?.substring(0, 255) || '',
          stateId,
        });
      }

      if (unresolvedStateCount > 0) {
        this.logger.warn(
          `${unresolvedStateCount} owners skipped — unresolved state abbreviation`,
        );
      }

      const result = await this.ownersService.upsertMany(toInsert);

      stats.created = result.length;
    } catch (error) {
      this.logger.error('Error syncing owners', error, {
        entityType: 'owners',
        count: owners.length,
      });
      console.error('[Warehouse Sync] owners error:', error);
      stats.errors = owners.length;
      throw error;
    }
  }

  private async syncStates(
    states: SnowflakeState[],
    stats: EntitySyncStats,
  ): Promise<void> {
    if (states.length === 0) {
      return;
    }

    try {
      const toInsert: StateInsert[] = states.map((state) => ({
        externalId: state.externalId,
        name: state.name?.substring(0, 255) || '',
        abbreviation: state.abbreviation?.substring(0, 10) || '',
      }));

      const result = await this.statesService.upsertMany(toInsert);
      stats.created = result.length;
    } catch (error) {
      this.logger.error('Error syncing states', error, {
        entityType: 'states',
        count: states.length,
      });
      console.error('[Warehouse Sync] states error:', error);
      stats.errors = states.length;
      throw error;
    }
  }

  private async syncGlobalVendors(
    globalVendors: SnowflakeGlobalVendor[],
    stats: EntitySyncStats,
  ): Promise<void> {
    if (globalVendors.length === 0) {
      return;
    }

    try {
      const toInsert: GlobalVendorInsert[] = globalVendors.map((gv) => ({
        externalId: gv.externalId,
        globalVendorName: gv.globalVendorName?.substring(0, 512) || '',
        globalNormalizedName:
          gv.globalNormalizedName?.substring(0, 512) ?? null,
      }));

      const result = await this.globalVendorsService.upsertMany(toInsert);
      stats.created = result.length;
    } catch (error) {
      this.logger.error('Error syncing global vendors', error, {
        entityType: 'globalVendors',
        count: globalVendors.length,
      });
      console.error('[Warehouse Sync] globalVendors error:', error);
      stats.errors = globalVendors.length;
      throw error;
    }
  }

  private async syncVendors(
    vendors: SnowflakeVendor[],
    stats: EntitySyncStats,
  ): Promise<void> {
    if (vendors.length === 0) {
      return;
    }

    try {
      const owners = await this.ownersService.getAllOwnersForWarehouseSync();
      const ownersByExternalId = new Map(
        owners.filter((o) => o.externalId).map((o) => [o.externalId, o.id]),
      );

      const globalVendorExternalIds = [
        ...new Set(
          vendors.map((v) => v.globalVendorExternalId).filter(Boolean),
        ),
      ];
      const globalVendorRows =
        await this.globalVendorsService.findByExternalIds(
          globalVendorExternalIds,
        );
      const globalVendorMap = new Map(
        globalVendorRows.map((gv) => [gv.externalId, gv.id]),
      );

      const toInsert: InsertVendor[] = vendors
        .map((vendor): InsertVendor | null => {
          const ownerId = ownersByExternalId.get(vendor.ownerExternalId);
          if (!ownerId) {
            this.logger.warn(
              `Vendor ${vendor.ownerVendorName} references unknown owner ${vendor.ownerExternalId}, skipping`,
            );
            return null;
          }

          return {
            externalId: vendor.externalId,
            ownerVendorName: vendor.ownerVendorName?.substring(0, 255) || '',
            ownerId,
            globalVendorId:
              globalVendorMap.get(vendor.globalVendorExternalId) ?? null,
          };
        })
        .filter((v): v is NonNullable<typeof v> => v !== null);

      const result = await this.vendorsService.upsertMany(toInsert);

      stats.created = result.length;
    } catch (error) {
      this.logger.error('Error syncing vendors', error, {
        entityType: 'vendors',
        count: vendors.length,
      });
      console.error('[Warehouse Sync] vendors error:', error);
      stats.errors = vendors.length;
      throw error;
    }
  }

  private async syncLettings(
    lettings: SnowflakeLetting[],
    stats: EntitySyncStats,
  ): Promise<void> {
    if (lettings.length === 0) {
      return;
    }

    try {
      const owners = await this.ownersService.getAllOwnersForWarehouseSync();
      const ownerMap = new Map(
        owners.filter((o) => o.externalId).map((o) => [o.externalId, o.id]),
      );

      const toInsert: InsertLetting[] = lettings
        .filter((letting) => ownerMap.has(letting.ownerExternalId))
        .map((letting) => ({
          externalId: letting.externalId,
          date: safeParseDate(letting.date) as Date,
          proposalCount: letting.proposalCount ?? 0,
          ownerId: ownerMap.get(letting.ownerExternalId) ?? '',
        }))
        .filter((letting) => letting.date !== null);

      const result = await this.lettingsService.upsertMany(toInsert);

      stats.created = result.length;
      stats.errors = lettings.length - toInsert.length;
    } catch (error) {
      this.logger.error('Error syncing lettings', error, {
        entityType: 'lettings',
        count: lettings.length,
      });
      console.error('[Warehouse Sync] lettings error:', error);
      stats.errors = lettings.length;
      throw error;
    }
  }

  private async syncItems(
    items: SnowflakeItem[],
    stats: EntitySyncStats,
  ): Promise<void> {
    if (items.length === 0) {
      return;
    }

    try {
      const owners = await this.ownersService.getAllOwnersForWarehouseSync();
      const ownerMap = new Map(
        owners.filter((o) => o.externalId).map((o) => [o.externalId, o.id]),
      );

      // Resolve category external IDs (Snowflake uid) → platform UUID
      const categoriesResult =
        await this.itemsService.getCategoryExternalIdMap();
      // Resolve unit external IDs (Snowflake uid) → platform UUID
      const unitsResult = await this.unitsService.getExternalIdMap();

      const toInsert: InsertItem[] = items
        .filter((item) => ownerMap.has(item.ownerExternalId))
        .map((item) => ({
          externalId: item.externalId,
          number: item.number?.substring(0, 255) || '',
          ownerItemDescription: item.ownerItemDescription ?? null,
          categoryId: item.categoryUid
            ? (categoriesResult.get(item.categoryUid) ?? null)
            : null,
          unit: item.unit?.substring(0, 50) ?? '',
          unitId: item.unitUid ? (unitsResult.get(item.unitUid) ?? null) : null,
          isTrueLumpSum: item.isTrueLumpSum ?? false,
          isCatchAll: item.isCatchAll ?? false,
          ownerId: ownerMap.get(item.ownerExternalId) ?? '',
        }));

      const result = await this.itemsService.upsertMany(toInsert);

      stats.created = result.length;
      stats.errors = items.length - toInsert.length;
    } catch (error) {
      this.logger.error('Error syncing items', error, {
        entityType: 'items',
        count: items.length,
      });
      console.error('[Warehouse Sync] items error:', error);
      stats.errors = items.length;
      throw error;
    }
  }

  private itemExternalIdCache: Map<string, string> | null = null;
  private itemExternalIdCacheOwnerId: string | undefined = undefined;
  private collectionCodeToIdCache: Map<string, string> | null = null;

  private async syncCollectionMappings(
    mappings: SnowflakeCollectionMapping[],
    stats: EntitySyncStats,
    ownerExternalId?: string,
  ): Promise<void> {
    if (mappings.length === 0) {
      return;
    }

    try {
      const ownerId = ownerExternalId
        ? (await this.ownersService.getAllOwnersForWarehouseSync()).find(
            (o) => o.externalId === ownerExternalId,
          )?.id
        : undefined;
      if (
        !this.itemExternalIdCache ||
        this.itemExternalIdCacheOwnerId !== ownerId
      ) {
        this.itemExternalIdCache =
          await this.itemCollectionsService.getAllItemExternalIdMap(ownerId);
        this.itemExternalIdCacheOwnerId = ownerId;
      }

      if (!this.collectionCodeToIdCache) {
        this.collectionCodeToIdCache =
          await this.itemCollectionsService.getAllCollectionCodeToIdMap();
      }

      // Resolve dbt's source_unit_uid (md5 of canonical code) → platform unit_id.
      // Symmetric to how items.unit_uid is resolved. Misses leave sourceUnitId
      // NULL; sourceUom text is still authoritative for current readers.
      const unitsExternalIdMap = await this.unitsService.getExternalIdMap();

      const toInsert: Array<{
        externalId: string;
        collectionId: string;
        itemId: string;
        sourceUom: string;
        sourceUnitId: string | null;
        conversionFactor: string;
        mappingSource: string;
      }> = [];

      for (const row of mappings) {
        const itemId = this.itemExternalIdCache.get(row.itemExternalId);
        if (!itemId) {
          continue;
        }
        const collectionId = this.collectionCodeToIdCache.get(
          row.collectionCode,
        );
        if (!collectionId) {
          continue;
        }
        toInsert.push({
          externalId: row.externalId,
          collectionId,
          itemId,
          sourceUom: row.sourceUom,
          sourceUnitId: row.sourceUnitUid
            ? (unitsExternalIdMap.get(row.sourceUnitUid) ?? null)
            : null,
          conversionFactor: row.conversionFactor,
          mappingSource: row.mappingSource,
        });
      }

      const synced =
        await this.itemCollectionsService.syncCollectionMappingsBulk(toInsert);
      stats.created = synced;
      stats.errors = mappings.length - toInsert.length;
    } catch (error) {
      this.logger.error('Error syncing collection mappings', error, {
        entityType: 'collectionMappings',
        count: mappings.length,
      });
      stats.errors = mappings.length;
      throw error;
    }
  }

  private async syncProposals(
    proposals: SnowflakeProposal[],
    stats: EntitySyncStats,
  ): Promise<void> {
    if (proposals.length === 0) {
      return;
    }

    try {
      const lettings =
        await this.lettingsService.getAllLettingsForWarehouseSync();
      const lettingMap = new Map(
        lettings.filter((l) => l.externalId).map((l) => [l.externalId, l.id]),
      );

      const owners = await this.ownersService.getAllOwnersForWarehouseSync();
      const ownerMap = new Map(
        owners.filter((o) => o.externalId).map((o) => [o.externalId, o.id]),
      );

      // The two classification dims sync BEFORE proposals (global references
      // run first), so every uid a proposal carries already resolves here. An
      // unresolved uid lands as null rather than failing the batch — a dim row
      // added mid-sync is picked up on the next run.
      const contractTypeMap =
        await this.proposalTypesService.getContractTypeIdsByExternalId();
      const projectTypeMap =
        await this.proposalTypesService.getProjectTypeIdsByExternalId();

      const toInsert: InsertProposal[] = proposals
        .filter(
          (p) =>
            lettingMap.has(p.lettingExternalId) &&
            ownerMap.has(p.ownerExternalId),
        )
        .map((proposal) => ({
          externalId: proposal.externalId,
          callNumber: proposal.callNumber?.substring(0, 255) || '',
          completionCalendarDays: proposal.completionCalendarDays ?? null,
          completionDate: safeParseDate(proposal.completionDate),
          completionDateIsDeduced: proposal.completionDateIsDeduced ?? false,
          completionDateSource:
            proposal.completionDateSource?.substring(0, 255) ?? null,
          completionWorkingDays: proposal.completionWorkingDays ?? null,
          contractId: (proposal.contractId ?? '').substring(0, 255),
          contractTypeId: proposal.contractTypeUid
            ? (contractTypeMap.get(proposal.contractTypeUid) ?? null)
            : null,
          projectTypeId: proposal.projectTypeUid
            ? (projectTypeMap.get(proposal.projectTypeUid) ?? null)
            : null,
          costEstimate: proposal.costEstimate,
          description: proposal.description ?? '',
          lettingDate: safeParseDate(proposal.lettingDate) as Date,
          projectNumber: proposal.projectNumber?.substring(0, 255) ?? null,
          route: (proposal.route ?? '').substring(0, 255),
          bidderCount: proposal.bidderCount,
          hasAwarded: proposal.hasAwarded ?? false,
          hasBidTabulation: proposal.hasBidTabulation ?? false,
          lettingId: lettingMap.get(proposal.lettingExternalId) ?? '',
          ownerId: ownerMap.get(proposal.ownerExternalId) ?? '',
        }))
        .filter((proposal) => proposal.lettingDate !== null);

      // Dedup check — Snowflake may emit duplicate UIDs in the same batch.
      // Log them and keep first occurrence to prevent ON CONFLICT batch failure.
      // Skip rows with null externalId — they aren't duplicates, just missing IDs.
      const extIdCounts = new Map<string, number>();
      for (const p of toInsert) {
        if (p.externalId == null) {
          continue;
        }
        extIdCounts.set(p.externalId, (extIdCounts.get(p.externalId) ?? 0) + 1);
      }
      const dupes = [...extIdCounts.entries()].filter(([, count]) => count > 1);
      let finalInsert = toInsert;
      if (dupes.length > 0) {
        console.warn(
          `[Proposals Sync] ${dupes.length} duplicate external_ids in batch:`,
        );
        for (const [extId, count] of dupes.slice(0, 10)) {
          const matching = toInsert.filter((p) => p.externalId === extId);
          console.warn(
            `  ${extId} (${count}x): contractId=${matching[0]?.contractId}, callNumbers=${matching.map((m) => m.callNumber).join(',')}`,
          );
        }
        const seen = new Set<string>();
        finalInsert = toInsert.filter((p) => {
          if (p.externalId == null) {
            return true;
          }
          if (seen.has(p.externalId)) {
            return false;
          }
          seen.add(p.externalId);
          return true;
        });
        console.warn(
          `[Proposals Sync] Deduped ${toInsert.length} → ${finalInsert.length} proposals`,
        );
      }

      const result = await this.proposalsService.upsertMany(finalInsert);

      const proposalIds = result.map((p) => p.id);
      const linked =
        await this.proposalsService.linkDraftExtensions(proposalIds);
      if (linked > 0) {
        this.logger.info(
          `Linked ${linked} draft proposal extensions to proposals`,
        );
      }

      stats.created = result.length;
      stats.errors = proposals.length - toInsert.length;
    } catch (error) {
      this.logger.error('Error syncing proposals', error, {
        entityType: 'proposals',
        count: proposals.length,
      });
      console.error('[Warehouse Sync] proposals error:', error);
      stats.errors = proposals.length;
      throw error;
    }
  }

  private async syncCounties(
    counties: SnowflakeCounty[],
    stats: EntitySyncStats,
  ): Promise<void> {
    if (counties.length === 0) {
      return;
    }

    try {
      const allStates = await this.statesService.getStates();
      const stateByExternalId = new Map(
        allStates.filter((s) => s.externalId).map((s) => [s.externalId, s.id]),
      );

      let unresolvedStateCount = 0;
      const toInsert: InsertCounty[] = counties.map((county) => {
        let stateId: string | null = null;
        if (county.stateExternalId) {
          stateId = stateByExternalId.get(county.stateExternalId) ?? null;
          if (!stateId) {
            unresolvedStateCount++;
          }
        }
        return {
          externalId: county.externalId,
          name: county.name?.substring(0, 255) || '',
          stateId,
        };
      });

      if (unresolvedStateCount > 0) {
        this.logger.warn(
          `${unresolvedStateCount} counties have unresolved stateExternalId references`,
          { entityType: 'counties', unresolvedStateCount },
        );
      }

      const result = await this.countiesService.upsertMany(toInsert);

      stats.created = result.length;
    } catch (error) {
      this.logger.error('Error syncing counties', error, {
        entityType: 'counties',
        count: counties.length,
      });
      console.error('[Warehouse Sync] counties error:', error);
      stats.errors = counties.length;
      throw error;
    }
  }

  private async syncProposalCounties(
    proposalCounties: SnowflakeProposalCounty[],
    stats: EntitySyncStats,
  ): Promise<void> {
    if (proposalCounties.length === 0) {
      return;
    }

    try {
      const toInsert = proposalCounties.map((pc) => ({
        proposalExternalId: pc.proposalExternalId,
        countyExternalId: pc.countyExternalId,
      }));

      const result =
        await this.proposalCountiesService.upsertManyFromWarehouse(toInsert);

      stats.created = result.synced;
      stats.errors = result.errors;
    } catch (error) {
      this.logger.error('Error syncing proposal counties', error, {
        entityType: 'proposalCounties',
        count: proposalCounties.length,
      });
      console.error('[Warehouse Sync] proposalCounties error:', error);
      stats.errors = proposalCounties.length;
      throw error;
    }
  }

  private async syncDistricts(
    districts: SnowflakeDistrict[],
    stats: EntitySyncStats,
  ): Promise<void> {
    if (districts.length === 0) {
      return;
    }

    try {
      const allStates = await this.statesService.getStates();
      const stateByExternalId = new Map(
        allStates.filter((s) => s.externalId).map((s) => [s.externalId, s.id]),
      );

      let unresolvedStateCount = 0;
      const toInsert: InsertDistrict[] = districts.map((district) => {
        let stateId: string | null = null;
        if (district.stateExternalId) {
          stateId = stateByExternalId.get(district.stateExternalId) ?? null;
          if (!stateId) {
            unresolvedStateCount++;
          }
        }
        return {
          externalId: district.externalId,
          code: district.identifier?.substring(0, 255) || '',
          name: district.name?.substring(0, 255) || '',
          label: district.label?.substring(0, 255) ?? null,
          stateId,
        };
      });

      if (unresolvedStateCount > 0) {
        this.logger.warn(
          `${unresolvedStateCount} districts have unresolved stateExternalId references`,
          { entityType: 'districts', unresolvedStateCount },
        );
      }

      const result = await this.districtsService.upsertMany(toInsert);

      stats.created = result.length;
    } catch (error) {
      this.logger.error('Error syncing districts', error, {
        entityType: 'districts',
        count: districts.length,
      });
      console.error('[Warehouse Sync] districts error:', error);
      stats.errors = districts.length;
      throw error;
    }
  }

  private async syncDistrictCounties(
    districtCounties: SnowflakeDistrictCounty[],
    stats: EntitySyncStats,
  ): Promise<void> {
    if (districtCounties.length === 0) {
      return;
    }

    try {
      const toInsert = districtCounties.map((dc) => ({
        districtExternalId: dc.districtExternalId,
        countyExternalId: dc.countyExternalId,
      }));

      const result =
        await this.districtCountiesService.upsertManyFromWarehouse(toInsert);

      stats.created = result.synced;
      stats.errors = result.errors;
    } catch (error) {
      this.logger.error('Error syncing district counties', error, {
        entityType: 'districtCounties',
        count: districtCounties.length,
      });
      console.error('[Warehouse Sync] districtCounties error:', error);
      stats.errors = districtCounties.length;
      throw error;
    }
  }

  private async syncProposalDistricts(
    proposalDistricts: SnowflakeProposalDistrict[],
    stats: EntitySyncStats,
  ): Promise<void> {
    if (proposalDistricts.length === 0) {
      return;
    }

    try {
      const toInsert = proposalDistricts.map((pd) => ({
        proposalExternalId: pd.proposalExternalId,
        districtExternalId: pd.districtExternalId,
        source: pd.source,
      }));

      const result =
        await this.proposalDistrictsService.upsertManyFromWarehouse(toInsert);

      stats.created = result.synced;
      stats.errors = result.errors;
    } catch (error) {
      this.logger.error('Error syncing proposal districts', error, {
        entityType: 'proposalDistricts',
        count: proposalDistricts.length,
      });
      console.error('[Warehouse Sync] proposalDistricts error:', error);
      stats.errors = proposalDistricts.length;
      throw error;
    }
  }

  private async syncProposalItems(
    proposalItems: SnowflakeProposalItem[],
    stats: EntitySyncStats,
  ): Promise<void> {
    if (proposalItems.length === 0) {
      return;
    }

    try {
      // Use database-level JOINs to resolve warehouse IDs instead of loading
      // all proposals and items into memory for lookup maps.
      const toInsert = proposalItems.map((pi) => ({
        externalId: pi.externalId,
        proposalExternalId: pi.proposalExternalId,
        itemExternalId: pi.itemExternalId,
        lineNumber: pi.lineNumber ?? null,
        itemNumber: pi.itemNumber ?? null,
        quantity: pi.quantity ?? null,
        costEstimate: pi.costEstimate ?? null,
        proposalItemDescription: pi.proposalItemDescription ?? null,
      }));

      const result = await this.proposalItemsService.upsertManyFromWarehouse(
        toInsert,
        { insertOnly: this.insertOnly },
      );

      stats.created = result.synced;
      stats.errors = result.errors;
    } catch (error) {
      this.logger.error('Error syncing proposal items', error, {
        entityType: 'proposalItems',
        count: proposalItems.length,
      });
      console.error('[Warehouse Sync] proposalItems error:', error);
      stats.errors = proposalItems.length;
      throw error;
    }
  }

  private async syncBids(
    bids: SnowflakeBid[],
    stats: EntitySyncStats,
  ): Promise<void> {
    if (bids.length === 0) {
      return;
    }

    try {
      // Use database-level JOINs to resolve warehouse IDs instead of loading
      // all vendors and proposals into memory for lookup maps.
      const toInsert = bids.map((bid) => ({
        externalId: bid.externalId,
        vendorExternalId: bid.vendorExternalId,
        proposalExternalId: bid.proposalExternalId,
        proposalVendorName: bid.proposalVendorName ?? null,
        deviation: bid.deviation ?? null,
        value: bid.value ?? null,
        winner: bid.winner ?? null,
        rank: bid.rank ?? null,
        extensionTotal: bid.extensionTotal ?? null,
        timeAdjustment: bid.timeAdjustment ?? null,
        evaluationAdjustment: bid.evaluationAdjustment ?? null,
        adjustedTotal: bid.adjustedTotal ?? null,
        deviationPercent: bid.deviationPercent ?? null,
      }));

      const result = await this.bidsService.upsertManyFromWarehouse(toInsert);

      stats.created = result.synced;
      stats.errors = result.errors;
    } catch (error) {
      this.logger.error('Error syncing bids', error, {
        entityType: 'bids',
        count: bids.length,
      });
      console.error('[Warehouse Sync] bids error:', error);
      stats.errors = bids.length;
      throw error;
    }
  }

  private async syncBidItems(
    bidItems: SnowflakeBidItem[],
    stats: EntitySyncStats,
  ): Promise<void> {
    if (bidItems.length === 0) {
      return;
    }

    try {
      const toInsert = bidItems.map((bi) => ({
        externalId: bi.externalId,
        bidExternalId: bi.bidExternalId,
        vendorExternalId: bi.vendorExternalId,
        proposalItemExternalId: bi.proposalItemExternalId,
        quantity: bi.quantity ?? null,
        lineNumber: bi.lineNumber ?? null,
        unitPrice: bi.unitPrice ?? null,
        extension: bi.extension ?? null,
        itemNumber: bi.itemNumber,
        proposalExternalId: bi.proposalExternalId,
        lettingExternalId: bi.lettingExternalId,
        bidValue: bi.bidValue ?? null,
        bidRank: bi.bidRank ?? null,
        bidWinner: bi.bidWinner ?? false,
        bidderCount: bi.bidderCount ?? null,
        lettingDate: bi.lettingDate ?? null,
        districtName: bi.districtName ?? null,
      }));

      const result = await this.bidItemsService.upsertManyFromWarehouse(
        toInsert,
        { insertOnly: this.insertOnly },
      );

      stats.created = result.synced;
      stats.errors = result.errors;
    } catch (error) {
      this.logger.error('Error syncing bid items', error, {
        entityType: 'bidItems',
        count: bidItems.length,
      });
      console.error('[Warehouse Sync] bidItems error:', error);
      stats.errors = bidItems.length;
      throw error;
    }
  }

  private async syncPayEstimates(
    rows: SnowflakePayEstimate[],
    stats: EntitySyncStats,
  ): Promise<void> {
    if (rows.length === 0) {
      return;
    }
    try {
      const result = await this.payEstimatesService.upsertManyFromWarehouse(
        rows,
        { insertOnly: this.insertOnly },
      );
      stats.created = result.synced;
      stats.errors = result.errors;
    } catch (error) {
      this.logger.error('Error syncing pay estimates', error, {
        entityType: 'payEstimates',
        count: rows.length,
      });
      stats.errors = rows.length;
      throw error;
    }
  }

  private async syncPayEstimateItems(
    rows: SnowflakePayEstimateItem[],
    stats: EntitySyncStats,
  ): Promise<void> {
    if (rows.length === 0) {
      return;
    }
    try {
      const result = await this.payEstimateItemsService.upsertManyFromWarehouse(
        rows,
        { insertOnly: this.insertOnly },
      );
      stats.created = result.synced;
      stats.errors = result.errors;
    } catch (error) {
      this.logger.error('Error syncing pay estimate items', error, {
        entityType: 'payEstimateItems',
        count: rows.length,
      });
      stats.errors = rows.length;
      throw error;
    }
  }

  private async syncBidItemLiquidAc(
    rows: SnowflakeBidItemLiquidAc[],
    stats: EntitySyncStats,
  ): Promise<void> {
    if (rows.length === 0) {
      return;
    }
    try {
      const result = await this.liquidAsphaltService.syncBidItemLiquidAc(
        rows.map(snowflakeBidItemLiquidAcToInput),
      );
      stats.created = result.upserted;
      stats.errors = 0;
    } catch (error) {
      this.logger.error('Error syncing liquid AC classifications', error, {
        entityType: 'bidItemLiquidAc',
        count: rows.length,
      });
      console.error('[Warehouse Sync] bidItemLiquidAc error:', error);
      stats.errors = rows.length;
      throw error;
    }
  }

  private async syncExceptions(
    exceptions: SnowflakeException[],
    stats: EntitySyncStats,
  ): Promise<void> {
    if (exceptions.length === 0) {
      return;
    }

    try {
      const toInsert = exceptions
        .filter((exc) => exc.proposalExternalId)
        .map((exc) => ({
          externalId: exc.externalId,
          proposalExternalId: exc.proposalExternalId as string,
          code: exc.exceptionCode,
          fields: exc.exceptionType || null,
          reason: exc.exceptionReason || null,
          exceptionLevel: exc.exceptionLevel || null,
          approvalStatus: exc.approvalStatus || null,
        }));

      const result =
        await this.proposalExceptionsService.upsertManyFromWarehouse(toInsert);

      stats.created = result.synced;
      stats.errors = result.errors;
    } catch (error) {
      this.logger.error('Error syncing exceptions', error, {
        entityType: 'exceptions',
        count: exceptions.length,
      });
      console.error('[Warehouse Sync] exceptions error:', error);
      stats.errors = exceptions.length;
      throw error;
    }
  }

  /**
   * Mark stale records as deleted for a specific owner and table.
   * Looks up the owner by external_id and calls the corresponding service's markStaleAsDeleted method.
   * @param ownerExternalId - The owner's external/warehouse ID
   * @param table - The table to mark stale records as deleted
   * @param syncStartTime - The timestamp when the sync started (records not updated after this are considered stale)
   * @returns Count of records marked as deleted
   */
  async markStaleRecordsAsDeleted(
    ownerExternalId: string,
    table: string,
    syncStartTime: Date,
  ): Promise<number> {
    // Look up owner by external_id
    const allOwners = await this.ownersService.getAllOwnersForWarehouseSync();
    const owner = allOwners.find((o) => o.externalId === ownerExternalId);

    if (!owner) {
      this.logger.warn(
        `Cannot mark stale records: owner not found with externalId: ${ownerExternalId}`,
      );
      return 0;
    }

    // Skip global/reference tables that don't belong to a specific owner
    if (
      [
        'owners',
        'states',
        'counties',
        'districts',
        'districtCounties',
      ].includes(table)
    ) {
      return 0;
    }

    // Call the appropriate service's markStaleAsDeleted method
    switch (table) {
      case 'vendors':
        return this.vendorsService.markStaleAsDeleted(owner.id, syncStartTime);
      case 'lettings':
        return this.lettingsService.markStaleAsDeleted(owner.id, syncStartTime);
      case 'items':
        return this.itemsService.markStaleAsDeleted(owner.id, syncStartTime);
      case 'proposals':
        return this.proposalsService.markStaleAsDeleted(
          owner.id,
          syncStartTime,
        );
      case 'proposalCounties':
        return this.proposalCountiesService.markStaleAsDeleted(
          owner.id,
          syncStartTime,
        );
      case 'proposalDistricts':
        return this.proposalDistrictsService.markStaleAsDeleted(
          owner.id,
          syncStartTime,
        );
      case 'proposalItems':
        return this.proposalItemsService.markStaleAsDeleted(
          owner.id,
          syncStartTime,
        );
      case 'bids':
        return this.bidsService.markStaleAsDeleted(owner.id, syncStartTime);
      case 'bidItems':
        return this.bidItemsService.markStaleAsDeleted(owner.id, syncStartTime);
      case 'bidItemLiquidAc':
        // Soft-delete drift: Snowflake's classification can change between
        // syncs (filter shifts, source row dropped), and the bid_items FK is
        // a no-op for our soft-delete model. Mirror every other per-owner
        // entity's stale-row pass so validation counts stay aligned.
        return this.liquidAsphaltService.markStaleAsDeleted(
          owner.id,
          syncStartTime,
        );
      case 'exceptions':
        return this.proposalExceptionsService.markStaleAsDeleted(
          owner.id,
          syncStartTime,
        );
      case 'collectionMappings':
        return this.itemCollectionsService.markStaleAsDeleted(
          owner.id,
          syncStartTime,
        );
      case 'payEstimates':
        return this.payEstimatesService.markStaleAsDeleted(
          owner.id,
          syncStartTime,
        );
      case 'payEstimateItems':
        return this.payEstimateItemsService.markStaleAsDeleted(
          owner.id,
          syncStartTime,
        );
      default:
        this.logger.warn(`Unknown table for soft-delete: ${table}`);
        return 0;
    }
  }

  async fetchMartTableTimestamps(): Promise<Map<string, Date>> {
    return this.warehouseRepository.fetchMartTableTimestamps();
  }

  async checkSyncLock(
    initialTimestamps: Map<string, Date>,
  ): Promise<{ safe: boolean; changedTables: string[] }> {
    const currentTimestamps =
      await this.warehouseRepository.fetchMartTableTimestamps();
    const changedTables: string[] = [];

    for (const [table, initialTs] of initialTimestamps) {
      const currentTs = currentTimestamps.get(table);
      if (!currentTs || currentTs.getTime() !== initialTs.getTime()) {
        changedTables.push(table);
      }
    }

    return {
      safe: changedTables.length === 0,
      changedTables,
    };
  }

  /**
   * Validate sync results by comparing Snowflake and PostgreSQL data.
   * Checks:
   * 1. Row counts match for all tables
   * 2. Bid items extension sum matches
   * 3. Bids with extension mismatches are the same in both sources
   */
  async validateSync(ownerExternalId: string): Promise<SyncValidationResult> {
    if (!this.syncJobRepository) {
      throw new Error('SyncJobRepository required for validation');
    }

    this.logger.info(`Validating sync for owner: ${ownerExternalId}`);

    // Fetch counts, extension sums, and bid mismatches from both sources in parallel
    const [
      snowflakeCounts,
      postgresqlCounts,
      snowflakeExtensionSum,
      postgresqlExtensionSum,
      snowflakeBidMismatches,
      postgresqlBidMismatches,
    ] = await Promise.all([
      this.warehouseRepository.fetchOwnerDataCounts(ownerExternalId),
      this.syncJobRepository.getPostgresqlCounts(ownerExternalId),
      this.warehouseRepository.fetchBidItemsExtensionSum(ownerExternalId),
      this.syncJobRepository.getBidItemsExtensionSum(ownerExternalId),
      this.warehouseRepository.fetchBidsWithExtensionMismatch(ownerExternalId),
      this.syncJobRepository.getBidsWithExtensionMismatch(ownerExternalId),
    ]);

    // Compare counts
    const TABLE_NAMES = [
      'owners',
      'states',
      'globalVendors',
      'vendors',
      'lettings',
      'items',
      'proposals',
      'counties',
      'districts',
      'districtCounties',
      'proposalCounties',
      'proposalDistricts',
      'proposalItems',
      'bids',
      'bidItems',
      'exceptions',
      // collectionMappings excluded — platform-created mappings cause expected count differences
    ] as const;
    type TableName = (typeof TABLE_NAMES)[number];

    const mismatches: TableName[] = [];
    for (const table of TABLE_NAMES) {
      const snowflakeCount =
        snowflakeCounts[table as keyof typeof snowflakeCounts];
      const postgresCount =
        postgresqlCounts[table as keyof typeof postgresqlCounts];

      if (snowflakeCount !== postgresCount) {
        this.logger.warn(
          `Count mismatch for ${table}: Snowflake=${snowflakeCount}, PostgreSQL=${postgresCount}`,
        );
        mismatches.push(table);
      }
    }

    // Compare extension sums (normalize decimal precision)
    const normalizeDecimal = (value: string): string => {
      if (!value.includes('.')) {
        return value;
      }
      return value.replace(/\.?0+$/, '');
    };
    const normalizedSnowflake = normalizeDecimal(snowflakeExtensionSum);
    const normalizedPostgres = normalizeDecimal(postgresqlExtensionSum);
    const extensionSumMatch = normalizedSnowflake === normalizedPostgres;

    if (!extensionSumMatch) {
      this.logger.warn(
        `Extension sum mismatch: Snowflake=${normalizedSnowflake}, PostgreSQL=${normalizedPostgres}`,
      );
    }

    // Compare bid extension mismatches
    const snowflakeBidIds = new Set(
      snowflakeBidMismatches.map((b) => b.bidExternalId),
    );
    const postgresqlBidIds = new Set(
      postgresqlBidMismatches.map((b) => b.bidExternalId),
    );
    const bidMismatchesMatch =
      snowflakeBidIds.size === postgresqlBidIds.size &&
      [...snowflakeBidIds].every((id) => postgresqlBidIds.has(id));

    if (!bidMismatchesMatch) {
      this.logger.warn(
        `Bid extension mismatches differ: Snowflake=${snowflakeBidMismatches.length}, PostgreSQL=${postgresqlBidMismatches.length}`,
      );
    }

    const isValid =
      mismatches.length === 0 && extensionSumMatch && bidMismatchesMatch;

    return {
      snowflakeCounts,
      postgresqlCounts,
      mismatches,
      extensionSum: {
        snowflakeSum: snowflakeExtensionSum,
        postgresqlSum: postgresqlExtensionSum,
        isMatch: extensionSumMatch,
      },
      bidExtensionMismatches: {
        snowflakeMismatches: snowflakeBidMismatches,
        postgresqlMismatches: postgresqlBidMismatches,
        isMatch: bidMismatchesMatch,
      },
      isValid,
    };
  }
}
